import { useState } from 'react';
import SubTaskList from './SubTaskList';
import { makeTask } from './utils';
import { themes } from './theme';

export default function TaskRow({
  task,
  idx,
  dayName,
  onChange,
  onDelete,
  onInsertTask,
  onTagPointerDown,
  onTagPointerEnter,
  onTagPointerUp,
  isDraggingTag,
  templates,
  theme = themes.light,
  onActivate,
  isMobile = false,
}) {
  const [showTplPicker, setShowTplPicker] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [editingTag, setEditingTag] = useState(false);

  function patch(p) { onChange({ ...task, ...p }); }

  function applyTemplate(tpl) {
    onChange({
      ...task,
      text: task.text || tpl.name,
      subtasks: JSON.parse(JSON.stringify(tpl.tasks)),
      expanded: true,
    });
    setShowTplPicker(false);
  }

  function toggleExpand() {
    if (!task.subtasks || task.subtasks.length === 0) {
      onChange({ ...task, subtasks: [makeTask()], expanded: true });
    } else {
      patch({ expanded: !task.expanded });
    }
  }

  const hasSubs = task.subtasks && task.subtasks.length > 0;
  const hasText = String(task.text || '').trim().length > 0;
  const hours = Number.isFinite(Number(task.hours)) ? Number(task.hours) : 0;
  const showHours = hasText || hours > 0;
  const updateHours = value => {
    const next = Math.max(0, Number(value) || 0);
    patch({ hours: Math.round(next * 4) / 4 });
  };

  if (task.type === 'tag') {
    return (
      <div
        data-day-name={dayName}
        data-task-index={idx}
        onPointerDown={e => {
          if (editingTag || e.target.closest('input, button, textarea')) return;
          e.preventDefault();
          onActivate?.();
          onTagPointerDown();
        }}
        onMouseDown={e => {
          if (editingTag || e.target.closest('input, button, textarea')) return;
          e.preventDefault();
          onActivate?.();
          onTagPointerDown();
        }}
        onDoubleClick={e => {
          if (e.target.closest('button')) return;
          e.preventDefault();
          e.stopPropagation();
          setEditingTag(true);
          onTagPointerUp();
        }}
        onPointerUp={onTagPointerUp}
        onPointerCancel={onTagPointerUp}
        style={{
          margin: '9px 0 6px',
          padding: '8px 9px',
          borderRadius: 7,
          border: `1px solid ${theme.primary}`,
          background: theme.mode === 'dark'
            ? `linear-gradient(135deg, rgba(184,121,255,0.20), rgba(34,211,238,0.10))`
            : `linear-gradient(135deg, ${theme.primarySoft}, ${theme.secondarySoft})`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          boxShadow: theme.mode === 'dark' ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : '0 10px 22px rgba(124,58,237,0.08)',
          cursor: isDraggingTag ? 'grabbing' : 'grab',
          opacity: isDraggingTag ? 0.62 : 1,
        }}
        onFocusCapture={onActivate}
      >
        <span
          title="Hold and drag tag up or down"
          onPointerDown={e => {
            e.preventDefault();
            e.stopPropagation();
            onActivate?.();
            onTagPointerDown();
          }}
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
            onActivate?.();
            onTagPointerDown();
          }}
          style={dragHandle(theme)}
        >⋮⋮</span>
        <span style={tagLabel(theme)}>TAG</span>
        <input
          value={task.text || ''}
          readOnly={!editingTag}
          onDoubleClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setEditingTag(true);
            onTagPointerUp();
          }}
          onMouseDown={e => {
            if (editingTag) {
              e.stopPropagation();
              return;
            }
            e.preventDefault();
            onActivate?.();
            onTagPointerDown();
          }}
          onBlur={() => setEditingTag(false)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur();
          }}
          onChange={e => patch({ text: e.target.value })}
          placeholder="Section name"
          style={{
            flex: 1,
            minWidth: 0,
            background: 'transparent',
            border: editingTag ? `1px solid ${theme.primary}` : 'none',
            borderRadius: 5,
            outline: 'none',
            color: theme.text,
            fontSize: 15,
            fontFamily: 'inherit',
            fontWeight: 800,
            cursor: editingTag ? 'text' : 'grab',
            userSelect: editingTag ? 'text' : 'none',
            padding: '3px 5px',
          }}
        />
        <button onClick={onInsertTask} title="Insert task below" style={tagButton(theme, false, isMobile)}>+TASK</button>
        <button onClick={onDelete} title="Delete tag" style={tagButton(theme, false, isMobile)}>X</button>
      </div>
    );
  }

  return (
    <div
      data-day-name={dayName}
      data-task-index={idx}
      onPointerEnter={onTagPointerEnter}
      onMouseEnter={onTagPointerEnter}
      onPointerUp={onTagPointerUp}
      style={{
        background: task.done
          ? theme.surfaceSoft
          : theme.taskSurface || (theme.mode === 'dark' ? 'rgba(17,22,41,0.92)' : 'rgba(255,255,255,0.94)'),
        border: '1px solid',
        borderColor: task.done ? theme.primarySoft : theme.borderSoft,
        borderRadius: 7,
        padding: '8px 9px',
        marginBottom: 7,
        transition: 'border-color 0.2s, background 0.2s, box-shadow 0.2s',
        boxShadow: task.done ? 'none' : theme.mode === 'dark' ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : '0 8px 20px rgba(36,55,99,0.07)',
      }}
      onMouseDown={onActivate}
      onFocusCapture={onActivate}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <button onClick={() => patch({ done: !task.done })} style={checkButton(task.done, theme)}>
          {task.done && <span style={{ color: '#ffffff', fontSize: 10, fontWeight: 900 }}>OK</span>}
        </button>

        <input
          value={task.text}
          onChange={e => patch({ text: e.target.value })}
          placeholder="Task"
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: task.done ? theme.faint : theme.text,
            textDecoration: task.done ? 'line-through' : 'none',
            fontSize: 14,
            fontFamily: 'inherit',
            fontWeight: task.text ? 600 : 500,
            minWidth: 0,
            flexBasis: isMobile ? 'calc(100% - 32px)' : 'auto',
          }}
        />

        {showHours && (
          <div title="Hours spent" style={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
            padding: '1px 3px',
            borderRadius: 6,
            border: `1px solid ${theme.borderSoft}`,
            background: theme.mode === 'dark' ? 'rgba(251,146,60,0.14)' : theme.accentSoft,
          }}>
            <button onClick={() => updateHours(hours - 0.5)} style={hourButton(theme)}>-</button>
            <input
              type="number"
              min="0"
              step="0.25"
              value={hours}
              onChange={e => updateHours(e.target.value)}
              aria-label="Hours spent"
              style={hourInput(theme)}
            />
            <button onClick={() => updateHours(hours + 0.5)} style={hourButton(theme)}>+</button>
          </div>
        )}

        {templates && templates.length > 0 && !task.done && (
          <div style={{ position: 'relative', marginLeft: isMobile ? 23 : 0 }}>
            <button
              title="Apply template"
              onClick={() => setShowTplPicker(v => !v)}
              style={iconButton(showTplPicker, theme)}
            >◆</button>

            {showTplPicker && (
              <div style={popover(theme, isMobile ? 'calc(100vw - 44px)' : 200, isMobile)}>
                <div style={{ color: theme.muted, fontSize: 10, marginBottom: 6, paddingLeft: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Apply template
                </div>
                {templates.map(tpl => (
                  <button key={tpl.id} onClick={() => applyTemplate(tpl)} style={templateItem(theme)}
                  onMouseEnter={e => e.currentTarget.style.background = theme.primarySoft}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >{tpl.name}</button>
                ))}
                <button onClick={() => setShowTplPicker(false)} style={{
                  marginTop: 4,
                  display: 'block',
                  width: '100%',
                  textAlign: 'center',
                  background: 'none',
                  border: 'none',
                  color: theme.faint,
                  fontSize: 10,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 700,
                }}>close</button>
              </div>
            )}
          </div>
        )}

        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={() => setShowActions(v => !v)}
            title="Task actions"
            style={actionMenuButton(showActions, theme)}
          >
            ...
          </button>
          {showActions && (
            <div style={actionMenu(theme)}>
              <button onClick={() => { toggleExpand(); setShowActions(false); }} style={menuItem(theme)}>
                {task.expanded ? '-SUB' : '+SUB'}
                {hasSubs ? ` ${task.subtasks.length}` : ''}
              </button>
              <button onClick={() => { patch({ showComment: !task.showComment }); setShowActions(false); }} style={menuItem(theme)}>
                NOTE{task.comment ? ' *' : ''}
              </button>
              <button onClick={() => { onDelete(); setShowActions(false); }} style={menuItem(theme, theme.danger)}>
                DELETE
              </button>
            </div>
          )}
        </div>
      </div>

      {task.showComment && (
        <textarea
          value={task.comment || ''}
          onChange={e => patch({ comment: e.target.value })}
          placeholder="Reason task is incomplete / notes..."
          rows={2}
          style={{
            width: '100%',
            marginTop: 6,
            background: theme.surfaceSoft,
            border: `1px solid ${theme.border}`,
            borderRadius: 6,
            color: theme.text,
            fontSize: 12,
            padding: '5px 10px',
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box',
            outline: 'none',
          }}
        />
      )}

      {task.expanded && hasSubs && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${theme.borderSoft}` }}>
          <SubTaskList
            tasks={task.subtasks || []}
            onChange={subs => patch({ subtasks: subs, expanded: subs.length > 0 })}
            depth={1}
            theme={theme}
          />
          <button
            onClick={() => patch({ subtasks: [...(task.subtasks || []), makeTask()], expanded: true })}
            style={{
              marginTop: 5,
              background: theme.secondarySoft,
              border: `1px dashed ${theme.secondary}`,
              borderRadius: 5,
              color: theme.secondary,
              fontSize: 12,
              cursor: 'pointer',
              padding: '3px 10px',
              fontFamily: 'inherit',
              fontWeight: 800,
            }}>+ subtask</button>
        </div>
      )}
    </div>
  );
}

function dragHandle(theme) {
  return {
    color: theme.faint,
    fontFamily: "'Orbitron', sans-serif",
    fontSize: 18,
    fontWeight: 900,
    letterSpacing: -3,
    flexShrink: 0,
    userSelect: 'none',
    width: 26,
    height: 28,
    borderRadius: 6,
    border: `1px solid ${theme.borderSoft}`,
    background: theme.surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'grab',
    touchAction: 'none',
  };
}

function tagLabel(theme) {
  return {
    color: theme.primary,
    fontFamily: "'Orbitron', sans-serif",
    fontSize: 12,
    fontWeight: 900,
    flexShrink: 0,
  };
}

function checkButton(done, theme) {
  return {
    width: 17,
    height: 17,
    borderRadius: 4,
    border: `2px solid ${done ? theme.success : theme.border}`,
    background: done ? theme.success : 'transparent',
    cursor: 'pointer',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

function iconButton(active, theme) {
  return {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: active ? theme.primary : theme.faint,
    fontSize: 15,
    padding: 2,
    fontWeight: 900,
    position: 'relative',
    minWidth: 18,
    fontFamily: "'Orbitron', sans-serif",
  };
}

function hourInput(theme) {
  return {
    width: 46,
    minWidth: 46,
    background: theme.surface,
    border: `1px solid ${theme.borderSoft}`,
    borderRadius: 4,
    outline: 'none',
    color: theme.text,
    fontSize: 12,
    fontFamily: "'DM Mono', monospace",
    fontWeight: 800,
    textAlign: 'center',
    padding: '1px 2px',
    boxSizing: 'border-box',
  };
}

function hourButton(theme) {
  return {
    width: 16,
    height: 16,
    borderRadius: 5,
    border: `1px solid ${theme.accent}`,
    background: theme.surface,
    color: theme.accent,
    cursor: 'pointer',
    fontSize: 11,
    lineHeight: '12px',
    fontWeight: 900,
    fontFamily: "'Orbitron', sans-serif",
    padding: 0,
  };
}

function tagButton(theme, disabled = false, isMobile = false) {
  return {
    border: `1px solid ${theme.border}`,
    background: disabled ? theme.surfaceSoft : theme.surface,
    color: disabled ? theme.faint : theme.primary,
    borderRadius: 5,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 10,
    fontWeight: 900,
    fontFamily: "'Orbitron', sans-serif",
    padding: isMobile ? '5px 8px' : '3px 6px',
    flexShrink: 0,
  };
}

function popover(theme, minWidth, isMobile = false) {
  return {
    position: 'absolute',
    right: isMobile ? 'auto' : 0,
    left: isMobile ? 0 : 'auto',
    top: 22,
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    zIndex: 200,
    minWidth,
    maxWidth: isMobile ? 'calc(100vw - 44px)' : 'min(260px, 80vw)',
    maxHeight: isMobile ? '48vh' : 'min(360px, 70vh)',
    overflowY: 'auto',
    padding: 8,
    boxShadow: theme.shadow,
    boxSizing: 'border-box',
    transform: isMobile ? 'translateX(-23px)' : 'none',
  };
}

function templateItem(theme) {
  return {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    color: theme.text,
    padding: '5px 8px',
    cursor: 'pointer',
    borderRadius: 4,
    fontSize: 12,
    fontFamily: 'inherit',
    fontWeight: 700,
  };
}

function actionMenuButton(active, theme) {
  return {
    width: 28,
    height: 24,
    borderRadius: 8,
    border: `1px solid ${active ? theme.primary : theme.border}`,
    background: active ? theme.primarySoft : theme.surfaceSoft,
    color: active ? theme.primary : theme.faint,
    cursor: 'pointer',
    fontSize: 15,
    lineHeight: '16px',
    fontWeight: 900,
    fontFamily: "'Orbitron', sans-serif",
    padding: 0,
  };
}

function actionMenu(theme) {
  return {
    position: 'absolute',
    right: 0,
    top: 28,
    zIndex: 250,
    minWidth: 132,
    padding: 6,
    borderRadius: 8,
    border: `1px solid ${theme.border}`,
    background: theme.surface,
    boxShadow: theme.shadowStrong,
    display: 'grid',
    gap: 5,
  };
}

function menuItem(theme, color = theme.text) {
  return {
    width: '100%',
    textAlign: 'left',
    border: `1px solid ${theme.borderSoft}`,
    background: theme.surfaceSoft,
    color,
    borderRadius: 6,
    cursor: 'pointer',
    padding: '5px 8px',
    fontSize: 11,
    fontFamily: "'Orbitron', sans-serif",
    fontWeight: 900,
  };
}
